import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Share2, Users, Check } from 'lucide-react';
import { Friendship } from '../types/friends';
import { motion, AnimatePresence } from 'framer-motion';
import { formatFriendDisplayName, getFriendInitial } from '../utils/friendDisplayUtils';

interface ShareCruiseModalProps {
  isOpen: boolean;
  onClose: () => void;
  cruiseId: string;
  friends: Friendship[];
  sharedWith: string[]; // Array of friendIds who can see this cruise
  onShare: (cruiseId: string, friendId: string) => Promise<void>;
  onUnshare: (cruiseId: string, friendId: string) => Promise<void>;
}

const ShareCruiseModal = ({
  isOpen,
  onClose,
  cruiseId,
  friends,
  sharedWith,
  onShare,
  onUnshare,
}: ShareCruiseModalProps) => {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Only show Restricted access friends (Full access friends see all cruises automatically)
  const restrictedAccessFriends = friends.filter(
    (friend) => friend.accessLevel === 'restricted-access'
  );

  const handleToggleShare = async (friendId: string, isShared: boolean) => {
    setIsLoading(friendId);
    setError('');
    try {
      if (isShared) {
        await onUnshare(cruiseId, friendId);
      } else {
        await onShare(cruiseId, friendId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update sharing');
    } finally {
      setIsLoading(null);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-teal-600" />
                  Share Cruise
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Full access friends automatically see all your cruises.
                    Only Restricted access friends need to be shared with individually.
                  </p>
                </div>

                {restrictedAccessFriends.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>You don't have any Restricted access friends.</p>
                    <p className="text-sm mt-1">
                      Full access friends automatically see all cruises.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      Select which Restricted access friends can see this cruise:
                    </p>
                    <div className="space-y-2">
                      {restrictedAccessFriends.map((friend) => {
                        const isShared = sharedWith.includes(friend.friendId);
                        return (
                          <label
                            key={friend.friendId}
                            className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                              isShared
                                ? 'border-secondary-500 bg-secondary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            } ${isLoading === friend.friendId ? 'opacity-50' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-secondary-600 flex items-center justify-center text-white font-semibold">
                                {getFriendInitial(friend)}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {formatFriendDisplayName(friend)}
                                </p>
                                <p className="text-sm text-gray-500">{friend.friendEmail}</p>
                              </div>
                            </div>
                            <div className="flex items-center">
                              {isShared && (
                                <Check className="h-5 w-5 text-secondary-600 mr-2" />
                              )}
                              <input
                                type="checkbox"
                                checked={isShared}
                                onChange={() => handleToggleShare(friend.friendId, isShared)}
                                disabled={isLoading === friend.friendId}
                                className="w-5 h-5 text-secondary-600 rounded focus:ring-secondary-500"
                              />
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="pt-4">
                  <button
                    onClick={onClose}
                    className="w-full px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors font-medium"
                  >
                    Done
                  </button>
                </div>
              </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default ShareCruiseModal;

